import Box from '@mui/material/Box';

import { ensureDefined } from '@e-socna/utils';
import { getFragmentData } from '@e-socna/data-layer';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import type {
  ReferencesContext_BusinessEventState,
  ReferencesContext_FolderState,
  ReferencesContext_MissionState,
} from '../../contexts/ReferencesContext';

import { NoData } from '../Illustrations/NoData';
import { FilterableCards } from '../FilterableCards';
import { DataGrid } from '../DataGrid';
import type { SplitButtonOption } from '../SplitButton';
import type { AccessGate } from '../Access/types/access-gate.type';

import { FolderBusinessEntityListCmp_FolderBusinessEntity_Fragment } from './fragments/folder-business-entity.fragment';
import { FolderBusinessEntityListVariant } from './enums/variant.enum';
import { FolderBusinessEntityCard } from './Card';
import { getColumns } from './get-columns';
import {
  BUSINESS_EVENT_SEARCH_CRITERIA,
  FOLDER_SEARCH_CRITERIA,
  MISSION_SEARCH_CRITERIA,
} from './utils/search-criteria.util';
import { NewFolderBusinessEntityCard } from './NewCard';

interface Props {
  readonly folderBusinessEntities: readonly FragmentType<
    typeof FolderBusinessEntityListCmp_FolderBusinessEntity_Fragment
  >[];
  readonly onStartCreate?: () => void;
  // Closes the creation when it is refused.
  readonly createGate?: AccessGate;
  readonly createOptions?: readonly SplitButtonOption[];
  readonly onCreateOption?: (option: Readonly<SplitButtonOption>) => void;
  // Overrides the creation card wording of the listed entities.
  readonly newFolderBusinessEntityString?: string;
  readonly variant: FolderBusinessEntityListVariant;
}

export const FolderBusinessEntityList = ({
  folderBusinessEntities: pFolderBusinessEntities,
  onStartCreate: handleStartCreate,
  createGate,
  createOptions,
  onCreateOption: handleCreateOption,
  newFolderBusinessEntityString: pNewFolderBusinessEntityString,
  variant,
}: Readonly<Props>) => {
  const {
    businessEventStates,
    businessEventStatesIdMap,
    folderStates,
    folderStatesIdMap,
    missionStates,
    missionStatesIdMap,
  } = useReferencesContext();

  const folderBusinessEntities = getFragmentData(
    FolderBusinessEntityListCmp_FolderBusinessEntity_Fragment,
    pFolderBusinessEntities,
  );

  // The parent (a tab panel) is a block: the empty list takes its whole height
  // to centre the illustration vertically.
  if (!folderBusinessEntities.length) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100%' }}>
        <NoData />
      </Box>
    );
  }

  const { typeName } = ensureDefined({
    typeName: folderBusinessEntities[0]?.__typename,
  });

  const configMap = {
    Folder: {
      searchCriteria: FOLDER_SEARCH_CRITERIA,
      folderBusinessEntityStates: folderStates,
      folderBusinessEntityStatesIdMap: folderStatesIdMap,
      searchBarPlaceholder:
        "Rechercher un code, une adresse, le nom d'un client, ...",
      newFolderBusinessEntityString: 'Créer un nouveau dossier',
      showSearchBar: true,
    },
    Mission: {
      searchCriteria: MISSION_SEARCH_CRITERIA,
      folderBusinessEntityStates: missionStates,
      folderBusinessEntityStatesIdMap: missionStatesIdMap,
      searchBarPlaceholder:
        "Rechercher un code, une adresse, le nom d'un client, ...",
      newFolderBusinessEntityString: 'Créer une nouvelle mission',
      showSearchBar: false,
    },
    BusinessEvent: {
      searchCriteria: BUSINESS_EVENT_SEARCH_CRITERIA,
      folderBusinessEntityStates: businessEventStates,
      folderBusinessEntityStatesIdMap: businessEventStatesIdMap,
      searchBarPlaceholder: "Rechercher une adresse, le nom d'un client, ...",
      newFolderBusinessEntityString: 'Créer un nouvel événement métier',
      showSearchBar: false,
    },
  };

  const {
    searchCriteria,
    folderBusinessEntityStates,
    folderBusinessEntityStatesIdMap,
    searchBarPlaceholder,
    newFolderBusinessEntityString,
    showSearchBar,
  } = configMap[typeName];

  const folderBusinessEntitiesWithState = folderBusinessEntities.map(
    (folderBusinessEntity) => {
      const { folderBusinessEntityState } = ensureDefined({
        folderBusinessEntityState: (
          folderBusinessEntityStatesIdMap as Record<
            Id,
            | ReferencesContext_FolderState
            | ReferencesContext_MissionState
            | ReferencesContext_BusinessEventState
          >
        )[folderBusinessEntity.stateId],
      });

      return { ...folderBusinessEntity, state: folderBusinessEntityState };
    },
  );

  if (variant === FolderBusinessEntityListVariant.Cards) {
    const createNewCard = handleStartCreate ? (
      <NewFolderBusinessEntityCard
        createOptions={createOptions}
        gate={createGate}
        newFolderBusinessEntityString={
          pNewFolderBusinessEntityString ?? newFolderBusinessEntityString
        }
        onCreateOption={handleCreateOption}
        onStartCreate={handleStartCreate}
      />
    ) : undefined;

    return (
      <FilterableCards
        Card={FolderBusinessEntityCard}
        createNewCard={createNewCard}
        id="folder-business-entity-card-list"
        items={folderBusinessEntitiesWithState}
        keys={searchCriteria}
        searchBarPlaceholder={searchBarPlaceholder}
        showSearchBar={showSearchBar}
      />
    );
  }

  return (
    <DataGrid
      columns={getColumns(folderBusinessEntityStates)}
      rows={folderBusinessEntities}
    />
  );
};
